import { useMemo } from 'react';

import { CSVRowsExportLimit } from '@portal/common/config';
import { DataFilters, DataGranularity } from '@portal/common/types';

import config from '../../../config';
import { getFiltersSelectionCount } from '../../../utility/data/calculate-combined-filters-count';
import { NO_BOUND_VALUE } from './CustomAgeGroupSelector';
import { AgeRange } from './ExportSingleAgeEstimatesModal';
import { AGE_GROUP_SEPARATOR } from './SavedAgeGroups';

const calculateFiltersCardinality = (
    filters: DataFilters,
    granularity: DataGranularity,
    ages: number
): number => getFiltersSelectionCount(filters, granularity) * ages;

export default (
    currentAgeGroup: AgeRange,
    savedAgeGroups: string[],
    selectedFilters: DataFilters,
    mergedSingleYearAgeGranularity: DataGranularity | null,
    isSingleAgesReport: boolean
) =>
    useMemo(() => {
        const calculateAges = (from: number | string, to: number | string): number => {
            if (from === 0 || to === NO_BOUND_VALUE) return 1; // Special case for no bound
            return Number(to) - Number(from) + 1;
        };

        const ages = isSingleAgesReport
            ? calculateAges(currentAgeGroup[0], currentAgeGroup[1])
            : Math.max(1, savedAgeGroups.length);

        const agesBackendCount = isSingleAgesReport
            ? ages
            : Math.max(
                  1,
                  savedAgeGroups.reduce((acc, ageGroup) => {
                      const [from, to] = ageGroup.split(AGE_GROUP_SEPARATOR).map(Number);
                      return acc + calculateAges(from, to);
                  }, 0)
              );

        const reportCardinality = calculateFiltersCardinality(
            selectedFilters,
            mergedSingleYearAgeGranularity || {},
            ages
        );

        const backendCardinality = calculateFiltersCardinality(
            selectedFilters,
            mergedSingleYearAgeGranularity || {},
            agesBackendCount
        );

        const isCardinalityLimitExceeded =
            reportCardinality > CSVRowsExportLimit ||
            backendCardinality > config.combinedFiltersCountPerQueryLimit;

        return {
            isCardinalityLimitExceeded,
            isValid: !isCardinalityLimitExceeded,
        };
    }, [
        currentAgeGroup,
        savedAgeGroups,
        selectedFilters,
        mergedSingleYearAgeGranularity,
        isSingleAgesReport,
    ]);
